import React from "react";
import iconMark from "@/assets/pnrbet-icon.png";

/* Canonical PRYMEBET playing card — the SINGLE card design used on every card
   surface (player live blackjack, live baccarat, dealer TV, operator console,
   admin). Rebuilt 1:1 from the "09 Dealer TV" / "08 Player table" mockups:

   Face-up  — off-white #FAFAF9, layered framed shadow, corner rank+suit indices
              only (Manrope rank + symbol-font suit, NO center pip), top-left and
              rotated bottom-right; red #EF4444 for ♥/♦, black #0A0A0A for ♠/♣.
   Face-down — dark #0C0C0E card with a green inset border, a green crosshatch
              panel and the PRYMEBET mark centred.
   Hands     — cards fan (±5°, pivoted low) and overlap. Use <CardHand/>.

   Everything scales from the card width so one component covers every size. */

const SUIT = { S: "♠", H: "♥", D: "♦", C: "♣", "?": "" };
const isRed = (s) => s === "H" || s === "D";
const SUIT_FONT = '"Segoe UI Symbol","Apple Symbols","Noto Sans Symbols2",sans-serif';

// Named widths (px); height derived at the mockup's 84:118 ratio.
const WIDTHS = { xs: 34, sm: 46, md: 60, lg: 84, xl: 108 };
const widthOf = (size) => (typeof size === "number" ? size : WIDTHS[size]) || WIDTHS.md;

export function PlayingCard({ card, faceDown = false, size = "md", rotate = 0, className = "", style }) {
  // Original tables own their responsive dimensions. Fill both flip faces
  // directly rather than scaling an 84px child inside a clipping container.
  const fill = size === "fill", w = widthOf(size);
  const scaled = ratio => fill ? `calc(var(--card-w) * ${ratio})` : w * ratio;
  const minimum = (value, ratio) => fill ? `max(${value}px, ${scaled(ratio)})` : Math.max(value, scaled(ratio));
  const h = fill ? "100%" : Math.round((w * 118) / 84);
  const radius = minimum(4, 10 / 84);
  const drop = fill ? `0 calc(var(--card-w) * .16) calc(var(--card-w) * .33) rgba(0,0,0,.55), 0 2px 6px rgba(0,0,0,.4)` : `0 ${Math.round(w * 0.16)}px ${Math.round(w * 0.33)}px rgba(0,0,0,.55), 0 2px 6px rgba(0,0,0,.4)`;
  const wrap = {
    width: fill ? "100%" : w, height: h, borderRadius: radius,
    transform: rotate ? `rotate(${rotate}deg)` : undefined,
    transformOrigin: "50% 130%",
    ...style,
  };

  if (faceDown || !card || card.rank === "?" || card.rank == null) {
    const frame = minimum(3, 5 / 84);
    return (
      <div className={`relative shrink-0 ${className}`} aria-label="Face-down card"
        style={{ ...wrap, background: "#0C0C0E", boxShadow: `inset 0 0 0 1px rgba(0,214,50,.45), inset 0 0 0 ${fill ? frame : `${frame}px`} #0C0C0E, ${drop}` }}>
        <div className="absolute" style={{
          inset: minimum(4, 6 / 84), borderRadius: scaled(7 / 84),
          background: "repeating-linear-gradient(45deg,rgba(0,214,50,.22) 0 1px,transparent 1px 8px),repeating-linear-gradient(-45deg,rgba(0,214,50,.22) 0 1px,transparent 1px 8px)",
        }} />
        <img src={iconMark} alt="" aria-hidden="true"
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          style={{ width: scaled(.42), height: scaled(.42), opacity: 0.92 }} />
      </div>
    );
  }

  const color = isRed(card.suit) ? "#EF4444" : "#0A0A0A";
  const pad = scaled(7 / 84);
  const idxW = scaled(26 / 84);
  const rankFs = scaled(25 / 84);
  const suitFs = scaled(19 / 84);
  const index = (
    <span className="flex flex-col items-center" style={{ width: idxW, gap: minimum(1, 2 / 84) }}>
      <span className="font-display font-extrabold" style={{ fontSize: rankFs, lineHeight: 1, letterSpacing: "-0.06em" }}>{card.rank}</span>
      <span style={{ fontSize: suitFs, lineHeight: 1, fontFamily: SUIT_FONT }}>{SUIT[card.suit]}</span>
    </span>
  );

  return (
    <div className={`relative shrink-0 ${className}`} aria-label={`${card.rank}${SUIT[card.suit]}`}
      style={{
        ...wrap, background: "#FAFAF9", color,
        boxShadow: `inset 0 0 0 1px rgba(10,10,10,.14), inset 0 0 0 ${fill ? minimum(2, 4 / 84) : `${minimum(2, 4 / 84)}px`} #FAFAF9, inset 0 0 0 ${fill ? minimum(3, 5 / 84) : `${minimum(3, 5 / 84)}px`} rgba(10,10,10,.06), ${drop}`,
      }}>
      <span className="absolute" style={{ top: pad, left: pad }}>{index}</span>
      <span className="absolute" style={{ bottom: pad, right: pad, transform: "rotate(180deg)" }}>{index}</span>
    </div>
  );
}

/* Overlapping, fanned hand — cards rotate around a low pivot and overlap, exactly
   like the mockup (e.g. dealer's up-card + face-down hole card). */
export function CardHand({ cards = [], size = "md", faceDownLast = false, className = "", overlap }) {
  const w = widthOf(size);
  const ov = overlap != null ? overlap : Math.round(w * 0.48);
  const n = cards.length;
  const step = n > 1 ? 5 : 0;
  return (
    <div className={`flex items-end ${className}`}>
      {cards.map((c, i) => {
        const rot = n > 1 ? (i - (n - 1) / 2) * step : 0;
        return (
          <div key={i} style={{ marginLeft: i === 0 ? 0 : -ov, zIndex: i + 1 }}>
            <PlayingCard card={c} size={size} rotate={rot} faceDown={faceDownLast && i === n - 1} />
          </div>
        );
      })}
    </div>
  );
}

export { SUIT as CARD_SUIT };
export default PlayingCard;
